import { BarChart3, FileText } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Firm } from '@/services/types/firms-types';

interface FirmDashboardProps {
  firm: Firm;
}

interface ActionCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

export const FirmDashboard: React.FC<FirmDashboardProps> = ({ firm }) => {
  const navigate = useNavigate();

  const actionCards: ActionCard[] = [
    {
      title: 'Generate Case Readout',
      description: "Run a case review for this firm's matters",
      icon: <BarChart3 className="h-5 w-5 text-blue-600" />,
      onClick: () =>
        navigate(
          `/bulk-finchgpt-actions/create?firm=${firm.id}&template=default-firm-case-review`,
        ),
    },
    {
      title: 'Firm Details',
      description: 'View and edit firm information',
      icon: <FileText className="h-5 w-5 text-gray-600" />,
      onClick: () => navigate(`/firms/${firm.id}/details`),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{firm.name}</h1>
        <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
          {firm.status && <span>Status: {firm.status}</span>}
          {firm.type && (
            <>
              <span>&middot;</span>
              <span>Type: {firm.type}</span>
            </>
          )}
          {firm.cms_system && (
            <>
              <span>&middot;</span>
              <span>CMS: {firm.cms_system}</span>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {actionCards.map((card) => (
          <Card
            key={card.title}
            className="cursor-pointer transition-colors hover:border-gray-300 hover:bg-gray-50/50"
            onClick={card.onClick}
          >
            <CardHeader>
              <div className="mb-2">{card.icon}</div>
              <CardTitle className="text-base">{card.title}</CardTitle>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
};
